import { createStore } from 'vuex'
import { schoolSelfStartMethod } from './selfStartMethods'

// The last chosen group, kept per user and school in this browser so it survives a reload or new login.
// Browser storage can be unavailable (private window, blocked site data), so every access is guarded.
const lastGroupKey = (user) => (user?.id && user?.schoolId ? `yuha.lastGroupId.${user.id}.${user.schoolId}` : '')

function readLastGroupId(user) {
  try {
    const key = lastGroupKey(user)
    return key ? localStorage.getItem(key) || '' : ''
  } catch {
    return ''
  }
}

function writeLastGroupId(user, groupId) {
  try {
    const key = lastGroupKey(user)
    if (key && groupId) localStorage.setItem(key, groupId)
  } catch {
    // Not stored; the group is still remembered for this session
  }
}

const store = createStore({
    state() {
      return {
        groupId: "",
        schoolId: "",
        showBsoFeatures: false,
        showSchoolFeatures: true,
        autoSettings: false,
        selfStart: false,
        selfStartMethod: 'portal',
        purchaseGames: false,
        betaTest: false,
        development: false,
        uxFlow: 'standard',
        pathId: "",
        developer: false,
        freshLogin: false,
        loginRedirect: "",
        admin: false,
        user: null,
        loggedUser: null,
        showMenu: false
      }
    },
    // Use mutations as the single write path for store state.
    // This keeps state updates predictable, easier to trace in devtools,
    // and avoids hidden side effects from direct state assignment in components.
    mutations: {
      SET_GROUP_ID(state, groupId) {
        state.groupId = groupId || ""
        writeLastGroupId(state.user, state.groupId)
      },
      SET_SCHOOL_ID(state, schoolId) {
        state.schoolId = schoolId || ""
      },
      SET_FRESH_LOGIN(state, isFreshLogin) {
        state.freshLogin = Boolean(isFreshLogin)
      },
      SET_LOGIN_REDIRECT(state, loginRedirect) {
        state.loginRedirect = loginRedirect || ""
      },
      SET_ADMIN(state, isAdmin) {
        state.admin = Boolean(isAdmin)
      },
      SET_USER(state, user) {
        state.user = user || null
      },
      MERGE_USER(state, payload) {
        state.user = {
          ...(state.user || {}),
          ...(payload || {})
        }
      },
      SET_LOGGED_USER(state, loggedUser) {
        state.loggedUser = loggedUser || null
      },
      SET_SHOW_MENU(state, showMenu) {
        state.showMenu = Boolean(showMenu)
      },
      SET_SHOW_BSO_FEATURES(state, showBsoFeatures) {
        state.showBsoFeatures = Boolean(showBsoFeatures)
      },
      SET_SHOW_SCHOOL_FEATURES(state, showSchoolFeatures) {
        state.showSchoolFeatures = Boolean(showSchoolFeatures)
      },
      SET_AUTO_SETTINGS(state, autoSettings) {
        state.autoSettings = Boolean(autoSettings)
      },
      // selfStart stays "start codes in use", which is what the student screens show
      SET_SELF_START_METHOD(state, method) {
        state.selfStartMethod = method
        state.selfStart = method === 'code'
      },
      SET_PURCHASE_GAMES(state, purchaseGames) {
        state.purchaseGames = Boolean(purchaseGames)
      },
      SET_BETA_TEST(state, betaTest) {
        state.betaTest = Boolean(betaTest)
      },
      SET_DEVELOPMENT(state, development) {
        state.development = Boolean(development)
      },
      SET_UX_FLOW(state, uxFlow) {
        state.uxFlow = uxFlow || 'standard'
      },
      SET_PATH_ID(state, pathId) {
        state.pathId = pathId || ""
      },
      SET_DEVELOPER(state, isDeveloper) {
        state.developer = Boolean(isDeveloper)
      }
    },
    actions: {
      applyAuthenticatedUser({ commit }, payload) {
        const {
          userData = {},
          authUid = "",
          isAdmin = false,
          isDeveloper = false,
          defaultAvatar = ""
        } = payload || {}

        // Keep legacy behavior: missing permission fields default to enabled.
        const manageStudents = Object.prototype.hasOwnProperty.call(userData, 'manageStudents')
          ? userData.manageStudents === true
          : true
        const manageGroups = Object.prototype.hasOwnProperty.call(userData, 'manageGroups')
          ? userData.manageGroups === true
          : manageStudents
        const normalizedUser = {
          ...userData,
          id: authUid,
          admin: Boolean(isAdmin),
          developer: Boolean(isDeveloper || userData.developer),
          avatar: userData.avatar || defaultAvatar,
          manageStudents,
          manageGroups,
          manageSubscription: userData.manageSubscription === true,
          manageUsers: userData.manageUsers === true,
          manageLocations: userData.manageLocations === true,
          contentCreator: userData.contentCreator === true,
          // Group management includes every group, and Yuha admins see every group of the school they are in
          showAllGroups: (isAdmin || manageGroups || userData.showAllGroups == null) ? true : userData.showAllGroups === true,
          groupIds: Array.isArray(userData.groupIds) ? userData.groupIds : [],
          // Location management includes every location; without the field nobody is limited to locations
          showAllLocations: userData.manageLocations === true || userData.showAllLocations !== false,
          locationIds: Array.isArray(userData.locationIds) ? userData.locationIds : []
        }

        commit('SET_USER', normalizedUser)
        commit('SET_SCHOOL_ID', normalizedUser.schoolId)
        // The group chosen last time in this browser, when the user can still see it; else their first own group
        const ownGroupIds = Array.isArray(normalizedUser.groupIds) ? normalizedUser.groupIds : []
        const lastGroupId = readLastGroupId(normalizedUser)
        const canSeeLastGroup = lastGroupId && (normalizedUser.showAllGroups || ownGroupIds.includes(lastGroupId))
        commit('SET_GROUP_ID', canSeeLastGroup ? lastGroupId : ownGroupIds[0] || "")
        commit('SET_ADMIN', normalizedUser.admin)
        commit('SET_DEVELOPER', normalizedUser.developer)
      },
      // Hydrate the per-school feature flags from a school document, so every
      // screen can read them from the store instead of re-fetching the doc.
      applySchoolSettings({ commit }, school = {}) {
        commit('SET_SHOW_BSO_FEATURES', school.isBso)
        commit('SET_SHOW_SCHOOL_FEATURES', school.isSchool)
        commit('SET_AUTO_SETTINGS', school.autoSettings || false)
        commit('SET_SELF_START_METHOD', schoolSelfStartMethod(school))
        commit('SET_PURCHASE_GAMES', school.purchaseGames || false)
        commit('SET_BETA_TEST', school.betaTest || false)
        commit('SET_DEVELOPMENT', school.development || false)
        commit('SET_UX_FLOW', school.uxFlow)
        commit('SET_PATH_ID', school.pathId)
      }
    }
});

// export the store
export default store